import type { FormattedError } from '@shared/utils/RequestUtils';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { SectionBox } from '@shared/components/ui/SectionBox';
import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import { useAuthStore } from '@stores/authStore';
import { ArrowLeft } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDefaultShippingCosts, useMissingDefaultShippingCosts } from '../../hooks';
import type {
  ProfitabilityDefaultShippingCost,
  ProfitabilityMissingDefaultShippingCost,
} from '../../models/Profitability';
import { formatMissingDefaultShippingServiceLabel } from '../../utils/defaultShippingCostsDisplay';
import { DefaultShippingCostsList, type DefaultShippingCostsListView } from '../display/DefaultShippingCostsList';
import {
  DefaultShippingCostFormModal,
  type DefaultShippingCostFormModalTarget,
} from '../interaction/DefaultShippingCostFormModal';

function isFormattedError(e: unknown): e is FormattedError {
  return typeof e === 'object' && e !== null && 'message' in e && typeof (e as FormattedError).message === 'string';
}

export const DefaultShippingCostsPage = () => {
  const { t, i18n } = useTranslation('profitabilityModule');
  const locale = i18n.language?.startsWith('en') ? 'en-GB' : 'es-ES';
  const navigate = useNavigate();
  const team = useAuthStore((s) => s.team);

  const [view, setView] = useState<DefaultShippingCostsListView>('missing');
  const [page, setPage] = useState(1);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formModalTarget, setFormModalTarget] = useState<DefaultShippingCostFormModalTarget | null>(null);

  const costsQuery = useDefaultShippingCosts(true);
  const missingQuery = useMissingDefaultShippingCosts(true, {
    page: view === 'missing' ? page : 1,
    per_page: view === 'missing' ? 20 : 1,
  });

  const breadcrumbs = useMemo(
    () => [{ label: t('title'), path: '/reports/profitability' }, { label: t('default_shipping_costs.page_title') }],
    [t],
  );

  const headerActions = useMemo(
    () => [
      {
        id: 'profitability-back-to-report',
        label: t('default_shipping_costs.back_to_overview'),
        onClick: () => navigate('/reports/profitability'),
        icon: <ArrowLeft className="h-4 w-4" />,
        variant: 'outline' as const,
      },
    ],
    [navigate, t],
  );

  usePageNavigation({ breadcrumbs, headerActions });

  const activeQuery = view === 'missing' ? missingQuery : costsQuery;
  const errorDescription = (() => {
    const e = activeQuery.error;
    if (!e || !isFormattedError(e)) return null;
    if (e.statusCode === 403) return e.message || t('errors.forbidden');
    return e.message || t('errors.generic');
  })();

  const hasBlockingError = !!errorDescription && activeQuery.isError && !activeQuery.data && !activeQuery.isLoading;

  const missingRows = view === 'missing' ? (missingQuery.data?.data ?? []) : [];
  const missingMeta = missingQuery.data?.meta;

  const openFormModal = (target: DefaultShippingCostFormModalTarget) => {
    setFormModalTarget(target);
    setFormModalOpen(true);
  };

  const handleConfigureMissing = (row: ProfitabilityMissingDefaultShippingCost) => {
    openFormModal({
      mode: 'create',
      team_id: row.team_id,
      team_name: row.team_name,
      courier_service_id: row.courier_service_id,
      serviceLabel: formatMissingDefaultShippingServiceLabel(row),
      hint: t('default_shipping_costs.configure_modal_hint'),
      returnToMissingOnSave: true,
    });
  };

  const handleEditConfigured = (row: ProfitabilityDefaultShippingCost, serviceLabel: string) => {
    openFormModal({
      mode: 'edit',
      id: row.id,
      team_id: row.team_id,
      courier_service_id: row.courier_service_id,
      serviceLabel,
      cost: row.cost,
      cod_fee_fixed: row.cod_fee_fixed ?? 0,
      cod_fee_percent: row.cod_fee_percent ?? 0,
    });
  };

  const handleAddConfigured = () => {
    const teamId = team?.id;
    if (teamId == null) return;
    openFormModal({
      mode: 'create',
      team_id: teamId,
      allowServiceSelect: true,
    });
  };

  return (
    <div className="space-y-6 px-6 py-4" data-testid="profitability-shipping-costs-page">
      {hasBlockingError ? (
        <EmptyView
          type="emptyState"
          icon={EMPTY_VIEW_ICONS.ORDERS}
          title={t('empty.error.title')}
          description={errorDescription ?? t('errors.generic')}
        />
      ) : (
        <SectionBox data-testid="profitability-shipping-costs-main">
          <div className="mb-4 space-y-1">
            <h2 className="text-lg font-semibold text-title">{t('default_shipping_costs.page_title')}</h2>
            <p className="text-sm text-muted-foreground">{t('default_shipping_costs.description')}</p>
          </div>

          <DefaultShippingCostsList
            view={view}
            onViewChange={(next) => {
              setView(next);
              setPage(1);
            }}
            missingTotal={missingMeta?.total}
            missingRows={missingRows}
            isLoading={view === 'missing' ? missingQuery.isLoading : costsQuery.isLoading}
            isError={view === 'missing' ? missingQuery.isError : costsQuery.isError}
            locale={locale}
            pagination={
              missingMeta
                ? {
                    currentPage: missingMeta.current_page,
                    lastPage: missingMeta.last_page,
                    total: missingMeta.total,
                    isFetching: missingQuery.isFetching,
                    onPrev: () => setPage((p) => Math.max(1, p - 1)),
                    onNext: () => setPage((p) => p + 1),
                  }
                : undefined
            }
            onConfigureMissing={handleConfigureMissing}
            onEditConfigured={handleEditConfigured}
            onAddConfigured={handleAddConfigured}
          />
        </SectionBox>
      )}

      <DefaultShippingCostFormModal
        open={formModalOpen}
        onOpenChange={setFormModalOpen}
        target={formModalTarget}
        onSaved={() => {
          if (formModalTarget?.returnToMissingOnSave) {
            setView('missing');
          }
        }}
      />
    </div>
  );
};
